import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import api from '../../services/api';

export const DevAiTest: React.FC = () => {
  const [prompt, setPrompt] = useState('Привіт, як тебе звати?');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [simpleLoading, setSimpleLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTestGenerate = async () => {
    setLoading(true);
    setError(null);
    setResponse('');
    try {
      const res = await api.post('/ai/generate', { prompt });
      const textResponse = res.data.data || res.data;
      setResponse(typeof textResponse === 'string' ? textResponse : JSON.stringify(textResponse, null, 2));
    } catch (err: any) {
      console.error('AI Test Error:', err);
      setError(err.response?.data?.message || err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleSimpleTest = async () => {
    setSimpleLoading(true);
    setError(null);
    setResponse('');
    try {
      const res = await api.get('/ai/simple-test');
      const textResponse = res.data.data || res.data;
      setResponse(typeof textResponse === 'string' ? textResponse : JSON.stringify(textResponse, null, 2));
    } catch (err: any) {
      console.error('Simple AI Test Error:', err);
      setError(err.response?.data?.message || err.message || 'Unknown error');
    } finally {
      setSimpleLoading(false);
    }
  };

  const handleListModels = async () => {
    setListLoading(true);
    setError(null);
    setResponse('');
    try {
      const res = await api.get('/ai/list-models');
      const textResponse = res.data.data || res.data;
      setResponse(JSON.stringify(textResponse, null, 2));
    } catch (err: any) {
      console.error('List Models Error:', err);
      setError(err.response?.data?.message || err.message || 'Unknown error');
    } finally {
      setListLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Тестування базового AI (Gemini)
      </Typography>

      <Stack spacing={2} sx={{ mb: 4 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            1. Діагностика (GET /ai/list-models)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Отримує список моделей, доступних для вашого API ключа. Допомагає перевірити права доступу та конфігурацію.
          </Typography>
          <Button 
            variant="outlined" 
            onClick={handleListModels}
            disabled={listLoading || loading || simpleLoading}
            color="secondary"
          >
            {listLoading ? <CircularProgress size={24} /> : 'Отримати список моделей'}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            2. Примітивний тест (GET /ai/simple-test)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Надсилає фіксований запит: "Hi, reply with one word: OK"
          </Typography>
          <Button 
            variant="outlined" 
            onClick={handleSimpleTest}
            disabled={simpleLoading || loading || listLoading}
          >
            {simpleLoading ? <CircularProgress size={24} /> : 'Запустити Simple Test'}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            3. Довільний запит (POST /ai/generate)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Запит до ШІ"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={loading || simpleLoading || listLoading}
              size="small"
            />
            <Button 
              variant="contained" 
              onClick={handleTestGenerate}
              disabled={loading || simpleLoading || listLoading || !prompt.trim()}
              sx={{ minWidth: '150px' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Відправити'}
            </Button>
          </Box>
        </Paper>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold">Помилка:</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {response && (
        <Paper sx={{ p: 2, bgcolor: '#f0fff4', border: '1px solid #c6f6d5', overflowX: 'auto' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Відповідь:</Typography>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {response}
          </Typography>
        </Paper>
      )}

      <Divider sx={{ my: 4 }} />
      
      <Typography variant="h6" gutterBottom>
        Поради щодо налагодження:
      </Typography>
      <Typography variant="body2" component="ul">
        <li>Перевірте вкладку <b>Network</b> у DevTools браузера.</li>
        <li>Переконайтеся, що на бекенді налаштований API ключ Gemini.</li>
        <li>Перевірте, чи додано <b>/api/v1</b> префікс у базовий URL.</li>
      </Typography>
    </Box>
  );
};
